import { emptyString } from '@/utils/validation'
import { type Template } from '@logistiek-zonder-papier/lzp-library'
import { computed, ref, type Ref } from 'vue'

export function useTemplateValidation(template: Ref<Template>) {
  const submitted = ref(false)

  const errors = computed(() => {
    const result: Record<string, string> = {}

    const data = template.value

    if (emptyString(data.name)) {
      result['name'] = 'Naam is verplicht'
    }

    if (emptyString(data.projectId ?? '')) {
      result['projectId'] = 'Project is verplicht'
    }

    if (data.type === 'bgb') {
      if (data.bgb == null) throw new Error('Bgb is undefined')

      if (emptyString(data.bgb.field1Role ?? '')) {
        result['bgb.field1Role'] = 'Rol is verplicht'
      }

      if (emptyString(data.bgb.field1.name)) {
        result['bgb.field1.name'] = 'Afzender is verplicht'
      }

      if (emptyString(data.bgb.field1.address.street)) {
        result['bgb.field1.address.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.bgb.field1.address.city)) {
        result['bgb.field1.address.city'] = 'Woonplaats is verplicht'
      }

      if (
        data.bgb.field2 &&
        !emptyString(
          data.bgb.field2.name ??
            '' +
              data.bgb.field2.address.street +
              data.bgb.field2.address.postalCode +
              data.bgb.field2.address.houseNumber +
              data.bgb.field2.address.suffix +
              data.bgb.field2.address.city
        )
      ) {
        if (emptyString(data.bgb.field2.name)) {
          result['bgb.field2.name'] = 'Sectie 2 kan niet half ingevuld zijn'
        }

        if (emptyString(data.bgb.field2.address.street)) {
          result['bgb.field2.address.street'] = 'Sectie 2 kan niet half ingevuld zijn'
        }

        if (emptyString(data.bgb.field2.address.city)) {
          result['bgb.field2.address.city'] = 'Sectie 2 kan niet half ingevuld zijn'
        }
      }

      if (emptyString(data.fromPerson.name)) {
        result['fromPerson.name'] = 'Ontdoener is verplicht'
      }

      if (emptyString(data.fromPerson.address.city)) {
        result['fromPerson.address.city'] = 'Woonplaats is verplicht'
      }

      if (emptyString(data.fromPerson.address.street)) {
        result['fromPerson.address.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.fromAddress.street2 ?? '')) {
        result['fromAddress.street2'] = 'Locatie van herkomst is verplicht'
      }

      if (emptyString(data.fromAddress.street)) {
        result['fromAddress.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.fromAddress.city)) {
        result['fromAddress.city'] = 'Woonplaats is verplicht'
      }

      if (
        data.bgb.field4A &&
        !emptyString(
          data.bgb.field4A.name ??
            '' +
              data.bgb.field4A.address.street +
              data.bgb.field4A.address.postalCode +
              data.bgb.field4A.address.houseNumber +
              data.bgb.field4A.address.suffix +
              data.bgb.field4A.address.city
        )
      ) {
        if (emptyString(data.bgb.field4A.name)) {
          result['bgb.field4A.name'] = 'Sectie 4a kan niet half ingevuld zijn'
        }

        if (emptyString(data.bgb.field4A.address.street)) {
          result['bgb.field4A.address.street'] = 'Sectie 4a kan niet half ingevuld zijn'
        }

        if (emptyString(data.bgb.field4A.address.city)) {
          result['bgb.field4A.address.city'] = 'Sectie 4a kan niet half ingevuld zijn'
        }
      }

      if (emptyString(data.toPerson.name)) {
        result['toPerson.name'] = 'Verwerker is verplicht'
      }

      if (emptyString(data.toAddress.street)) {
        result['toAddress.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.toAddress.street2 ?? '')) {
        result['toAddress.street2'] = 'Locatie van bestemming is verplicht'
      }

      if (emptyString(data.toAddress.city)) {
        result['toAddress.city'] = 'Woonplaats is verplicht'
      }

      if (emptyString(data.bgb.field5.name)) {
        result['bgb.field5.name'] = 'Ontvanger/inzamelaar/vervoerder is verplicht'
      }

      if (emptyString(data.bgb.field5.address.street)) {
        result['bgb.field5.address.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.bgb.field5.address.city)) {
        result['bgb.field5.address.city'] = 'Woonplaats is verplicht'
      }

      if (data.bgb.groupId.trim().length !== 12) {
        result['bgb.groupId'] = 'Afvalstroomnummer moet 12 tekens zijn'
      }

      if (emptyString(data.bgb.waybillItems[0]?.name ?? '')) {
        result['bgb.waybillItems.0.name'] = 'Product naam is verplicht'
      }

      if (emptyString(data.bgb.waybillItems[0]?.euralCode ?? '')) {
        result['bgb.waybillItems.0.euralCode'] = 'Eural code is verplicht'
      }

      if (emptyString(data.bgb.waybillItems[0]?.packagingType ?? '')) {
        result['bgb.waybillItems.0.packagingType'] = 'Verpakkings type is verplicht'
      }

      if (emptyString(data.bgb.waybillItems[0]?.processingMethod ?? '')) {
        result['bgb.waybillItems.0.processingMethod'] = 'Verwerkings methode is verplicht'
      }
    }

    if (data.type === 'ecmr') {
      if (data.ecmr == null) throw new Error('Ecmr is undefined')

      if (emptyString(data.ecmr.carrierCode)) {
        result['ecmr.carrierCode'] = 'Vervoerder Code is verplicht'
      }

      if (emptyString(data.fromPerson.name)) {
        result['fromPerson.name'] = 'Afzender is verplicht'
      }

      if (emptyString(data.fromPerson.address.city)) {
        result['fromPerson.address.city'] = 'Stad is verplicht'
      }

      if (emptyString(data.fromPerson.address.street)) {
        result['fromPerson.address.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.fromPerson.address.country)) {
        result['fromPerson.address.country'] = 'Land is verplicht'
      }

      if (emptyString(data.fromAddress.street2 ?? '')) {
        result['fromAddress.street2'] = 'Plaats van inontvangstname is verplicht'
      }

      if (emptyString(data.fromAddress.street)) {
        result['fromAddress.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.fromAddress.city)) {
        result['fromAddress.city'] = 'Stad is verplicht'
      }

      if (emptyString(data.fromAddress.country)) {
        result['fromAddress.country'] = 'Land is verplicht'
      }

      if (emptyString(data.toPerson.name)) {
        result['toPerson.name'] = 'Afzender is verplicht'
      }

      if (emptyString(data.toPerson.address.city)) {
        result['toPerson.address.city'] = 'Stad is verplicht'
      }

      if (emptyString(data.toPerson.address.street)) {
        result['toPerson.address.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.toPerson.address.country)) {
        result['toPerson.address.country'] = 'Land is verplicht'
      }

      if (emptyString(data.toAddress.street2 ?? '')) {
        result['toAddress.street2'] = 'Plaats van inontvangstname is verplicht'
      }

      if (emptyString(data.toAddress.street)) {
        result['toAddress.street'] = 'Straat is verplicht'
      }

      if (emptyString(data.toAddress.city)) {
        result['toAddress.city'] = 'Stad is verplicht'
      }

      if (emptyString(data.toAddress.country)) {
        result['toAddress.country'] = 'Land is verplicht'
      }

      if (emptyString(data.ecmr.documents ?? '')) {
        result['ecmr.documents'] = 'Documenten is verplicht'
      }

      if (emptyString(data.ecmr.carrier.name)) {
        result['ecmr.carrier.name'] = 'Vervoerder is verplicht'
      }

      if (emptyString(data.ecmr.carrier.address.street)) {
        result['ecmr.carrier.address.street'] = 'Staat is verplicht'
      }

      if (emptyString(data.ecmr.carrier.address.city)) {
        result['ecmr.carrier.address.city'] = 'Stad is verplicht'
      }

      if (emptyString(data.ecmr.carrier.address.country)) {
        result['ecmr.carrier.address.country'] = 'Country is verplicht'
      }

      if (
        !emptyString(
          data.ecmr.carrier2.name +
            data.ecmr.carrier2.address.street +
            data.ecmr.carrier2.address.postalCode +
            data.ecmr.carrier2.address.houseNumber +
            data.ecmr.carrier2.address.suffix +
            data.ecmr.carrier2.address.country +
            data.ecmr.carrier2.address.city
        )
      ) {
        if (emptyString(data.ecmr.carrier2.name)) {
          result['ecmr.carrier2.name'] = 'Opvolgende voervoerders is verplicht'
        }

        if (emptyString(data.ecmr.carrier2.address.street)) {
          result['ecmr.carrier2.address.street'] = 'Staat is verplicht'
        }

        if (emptyString(data.ecmr.carrier2.address.city)) {
          result['ecmr.carrier2.address.city'] = 'Stad is verplicht'
        }

        if (emptyString(data.ecmr.carrier2.address.country)) {
          result['ecmr.carrier2.address.country'] = 'Country is verplicht'
        }
      }
      if (data.ecmr.products.length === 0) {
        result['ecmr.products'] = 'Minimaal 1 product is verplicht'
      }

      for (let index = 0; index < data.ecmr.products.length; index++) {
        const product = data.ecmr.products[index]

        if (emptyString(product?.numbers ?? '')) {
          result[`ecmr.products.${index}.numbers`] = 'Merken en nummers is verplicht'
        }

        if (emptyString(String(product?.amount)) || (product?.amount ?? 0) <= 0) {
          result[`ecmr.products.${index}.amount`] = 'Aantal coli moet groter dan 0 zijn'
        }

        if (emptyString(product?.packaging ?? '')) {
          result[`ecmr.products.${index}.packaging`] = 'Wijze van verpakking is verplicht'
        }

        if (emptyString(product?.natureOfGoods ?? '')) {
          result[`ecmr.products.${index}.natureOfGoods`] = 'Aard der goederen is verplicht'
        }

        if (emptyString(product?.statisticalNumber ?? '')) {
          result[`ecmr.products.${index}.statisticalNumber`] = 'Statistisch nummer is verplicht'
        }

        if (emptyString(String(product?.grossWeightKg)) || (product?.grossWeightKg ?? 0) <= 0) {
          result[`ecmr.products.${index}.grossWeightKg`] =
            'Bruto gewicht in kg moet groter dan 0 zijn'
        }

        if (emptyString(String(product?.volumeM3)) || (product?.volumeM3 ?? 0) <= 0) {
          result[`ecmr.products.${index}.volumeM3`] = 'Volume in m3 moet groter dan 0 zijn'
        }
      }

      if (data.ecmr.draftedAt == null) {
        result['ecmr.draftedAt'] = 'Datum is verplicht'
      }

      if (emptyString(data.ecmr.draftedAtAddress)) {
        result['ecmr.draftedAtAddress'] = 'Plaats is verplicht'
      }
    }

    return result
  })

  const visibleErrors = computed<Record<string, string>>(() => {
    if (!submitted.value) return {}
    return errors.value
  })

  const isValid = computed(() => Object.keys(errors.value).length === 0)
  const isSubmittedAndInvalid = computed(() => submitted.value && !isValid.value)

  function validate(): boolean {
    submitted.value = true
    return isValid.value
  }

  return { errors, visibleErrors, isValid, isSubmittedAndInvalid, validate }
}
